import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';

import { environment } from 'src/environments/environment';
import { Perk, PerkVoucherCode, WidgetSize } from 'src/app/interfaces/perk';
import { Pet } from 'src/app/interfaces/pet';
import { User } from 'src/app/interfaces/user';

import { UserService } from '../user/user.service';
import { PetplanTrialApplication } from 'src/app/interfaces/petplan';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PerkService {
  // MARK: - Properties

  petplanEligiblePets: Array<Pet> = [];
  userOptions: any;

  // MARK: - Intialization

  constructor(private http: HttpClient, private userService: UserService) {}

  // MARK: - Public methods

  public getPerks = (user: User): Observable<Perk[]> => {
    return this.getPerksFromAPI(user).pipe(
      map(
        (perkData) => this.replaceThePerkPosition(perkData.sort((a, b) => a.order - b.order)) // Sorting the array in ascending order by the 'order' field
      ),
      catchError((err) => {
        console.error(err);
        return of([]);
      })
    );
  };

  public getPetplanEligiblePets(): Observable<Pet[]> {
    return this.http.get<any>(`${environment.apiUrl}/api/v2/perks/petplantrial/user/me/pets`);
  }

  public sendPetplanCoverNoteEmail(): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}/api/users/me/petplan/send-covernote-email`,
      null
    );
  }

  public sendPetplanProductEmail(): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}/api/users/me/petplan/send-product-email`,
      null
    );
  }

  public submitPetplanTrialApplication(
    petplanApplication: PetplanTrialApplication
  ): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}/api/v2/petplan/trial`,
      petplanApplication
    );
  }

  public getPerkVoucerCode(shortCode: string): Observable<PerkVoucherCode> {
    return this.http.get<PerkVoucherCode>(
      `${environment.apiUrl}/api/v2/perks/${shortCode}/voucher`
    );
  }

  // MARK: - Private methods

  private getPerksFromAPI(user: User): Observable<Perk[]> {
    return this.http.get<Perk[]>(`${environment.apiUrl}/api/v2/perks/user/${user.id}`);
  }

  private perkNameExists(filteredPerks: Perk[], perkName: string): boolean {
    return filteredPerks.some((p) => p.name === perkName);
  }

  private replaceThePerkPosition(list: Array<Perk>): Array<Perk> {
    const isFirstPerkLarge = list[0].widget.widgetSize === WidgetSize.Large;
    const rowsToCount = 5;
    const correctingTheEffectOfTheLargePerk = 1;

    const petPlanTrialCheck =
      this.perkNameExists(list, 'petplan-trial-dog') ||
      this.perkNameExists(list, 'petplan-trial-cat');

    const largePerkSize = list.filter((perk) => perk.widget.widgetSize === WidgetSize.Large);
    const largePerkSizeCount = largePerkSize.length;

    const mediumPerkSize = list.filter((perk) => perk.widget.widgetSize === WidgetSize.Medium);
    const mediumPerkSizeCount = mediumPerkSize.length;
    const rowsWihtMediumSizePerk = Math.ceil(mediumPerkSizeCount / rowsToCount);
    const rowsWihtMediumSizePerkWithoutLargePerk = Math.floor(mediumPerkSizeCount / rowsToCount);
    const bottomLeftPerk =
      largePerkSizeCount !== 0
        ? rowsWihtMediumSizePerk * rowsToCount - correctingTheEffectOfTheLargePerk
        : rowsWihtMediumSizePerkWithoutLargePerk * rowsToCount;

    list.forEach((perk) => {
      let indexNumber = 0;

      const currentIndexOfObject = list.indexOf(perk);

      if (perk.filters.perkApparencePosition === 0) {
        indexNumber = isFirstPerkLarge ? 1 : 4;
      }

      if (perk.filters.perkApparencePosition === 1) {
        indexNumber = bottomLeftPerk - 1;
      }

      if (perk.filters.perkApparencePosition === 2) {
        indexNumber = petPlanTrialCheck ? (isFirstPerkLarge ? 1 : 4) : bottomLeftPerk;
      }

      if (perk.widget.widgetSize === WidgetSize.Small) {
        perk.widget.description = '';
      }

      // Check if the indexNumber is within the current array size
      if (indexNumber >= 0 && indexNumber <= list.length) {
        // Copy the list
        const newList = [...list];
        // Remove the object from the original position
        newList.splice(currentIndexOfObject, 1);
        // Insert it into the new position
        newList.splice(indexNumber, 0, perk);
        // Check if the removal and insertion were successful
        if (newList[indexNumber] === perk) {
          // Return the updated list
          list = newList;
        } else {
          console.error('An error occurred while moving the object.');
        }
      } else {
        console.error('The specified indexNumber is invalid.');
      }
    });
    // Return the original list if any error occurs
    return list.reverse();
  }
}
